import React from 'react';
import { Building2, Hospital, Sparkles, Box, Building } from 'lucide-react';
import { Link } from 'react-router-dom';

const ServicesSection = () => {
  const services = [
    {
      icon: Building2,
      title: 'Construction',
      path: '/construction-progress-capture',
      description: 'Dated Matterport progress records, point cloud surveys and gridline and datum checks for live construction projects. From £495 per visit.',
      color: 'from-blue-500 to-cyan-500',
      image: '/images/construction-site-scanning-800.webp', srcSet: '/images/construction-site-scanning-480.webp 480w, /images/construction-site-scanning-800.webp 800w, /images/construction-site-scanning-1200.webp 1200w', width: 800, height: 450,
      alt: '3D laser scanning and point cloud capture on a construction site',
    },
    {
      icon: Building,
      title: 'Commercial Real Estate',
      path: '/commercial-property-3d-tours-london',
      description: 'Matterport tours, floor plans and photos of vacant units, offices, hotels and shops for agents, landlords and fit-out teams. Fixed price per unit, live in 48 hours.',
      color: 'from-purple-500 to-pink-500',
      image: '/images/modern-office-800.webp', srcSet: '/images/modern-office-480.webp 480w, /images/modern-office-800.webp 800w, /images/modern-office-1200.webp 1200w', width: 800, height: 534,
      alt: 'Modern commercial office captured for a Matterport virtual tour',
    },
    {
      icon: Hospital,
      title: 'Healthcare',
      description: 'Supporting patients and families through their healthcare journey. Virtual tours help reduce anxiety and improve preparedness for clinical environments.',
      color: 'from-green-500 to-emerald-500',
      image: '/images/nhs-hospital-800.webp', srcSet: '/images/nhs-hospital-480.webp 480w, /images/nhs-hospital-800.webp 800w, /images/nhs-hospital-1200.webp 1200w', width: 800, height: 548,
      alt: 'NHS hospital environment for 3D virtual tour scanning',
    },
    {
      icon: Building2,
      title: 'Leisure',
      description: 'Comprehensive 3D scanning solutions for hotels, spas, resorts and hospitality venues.',
      color: 'from-yellow-500 to-orange-500',
      image: '/images/luxury-spa-800.webp', srcSet: '/images/luxury-spa-480.webp 480w, /images/luxury-spa-800.webp 800w, /images/luxury-spa-1200.webp 1200w', width: 800, height: 533,
      alt: 'Luxury spa and hospitality venue for Matterport 3D virtual tours',
    },
    {
      icon: Box,
      title: 'CGI Staging',
      description: 'Photorealistic CGI and visualization for commercial fit-out and heritage projects.',
      color: 'from-red-500 to-pink-500',
      image: '/images/cgi-villa-interior-800.webp', srcSet: '/images/cgi-villa-interior-480.webp 480w, /images/cgi-villa-interior-800.webp 800w, /images/cgi-villa-interior-1200.webp 1200w', width: 800, height: 530,
      alt: 'Photorealistic CGI visualisation of a luxury villa interior by Digital Scanning Group',
    },
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-full mb-6">
            <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
            <span className="text-blue-600 text-sm font-semibold">OUR SERVICES</span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            Comprehensive Digital Solutions
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            We deliver accurate, visual, and defensible digital records using advanced 3D scanning and immersive technologies.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <div
                key={index}
                className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
              >
                {/* Image Background */}
                <div className="relative h-64 overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent z-10"></div>
                  <img
                    src={service.image}
                    srcSet={service.srcSet}
                    sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                    width={service.width}
                    height={service.height}
                    loading="lazy"
                    decoding="async"
                    alt={service.alt || service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  {/* Icon */}
                  <div className="absolute top-6 left-6 z-20">
                    <div className={`p-4 bg-gradient-to-br ${service.color} rounded-xl shadow-xl`}>
                      <Icon className="h-8 w-8 text-white" />
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                    {service.path ? <Link to={service.path}>{service.title}</Link> : service.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">{service.description}</p>
                  {service.path ? (
                    <Link to={service.path} className="mt-6 text-blue-600 font-semibold flex items-center gap-2 group-hover:gap-4 transition-all">
                      Learn More
                      <span className="text-xl">→</span>
                    </Link>
                  ) : (
                    <button className="mt-6 text-blue-600 font-semibold flex items-center gap-2 group-hover:gap-4 transition-all">
                      Learn More
                      <span className="text-xl">→</span>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;